import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  Space,
  Input,
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Select,
  Card,
  Row,
  Col,
  Tooltip,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  PictureOutlined,
  InfoCircleOutlined,
  ShoppingOutlined,
  DollarCircleOutlined,
  FileTextOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { getProducts, createProduct, updateProduct, deleteProduct, getSingleProduct } from '../../api/api';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import NotificationContext from '../../../../providers/NotificationProvider';
import { showSuccessToast, showErrorToast } from '../../../../components/commonComponents/gxAntdToast/toastUtils';
import { useFileUploader } from '../../../../hooks/useFileUploader';
import Eye from '../../../../assets/customSVG/Eye';
import Edit from '../../../../assets/customSVG/Edit';
import CustomDelete from '../../../../assets/customSVG/CustomDelete';
import SearchBar from '../../../../components/commonComponents/searchBar';
import { useFeature } from '../../../../hooks/useFeatureGating';
import { FEATURES } from '../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';
import { cn } from '../../../../helper/cn';

const MAX_IMAGE_SIZE = 16 * 1024 * 1024; // 16MB

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // NOTIFICATION(Toast)
  const notification = useContext(NotificationContext);

  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const { currentBrand } = useSelector((state) => state.app);

  const {
    uploadFiles,
    responseFile: uploadedFileUrl,
    error: uploadError,
    uploading,
  } = useFileUploader();

  const [fileList, setFileList] = useState([]);
  const [imageError, setImageError] = useState('');
  const defaultImageHelperText = 'Upload PNG or JPG files up to 16MB. Square images (minimum 320px) look best.';
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { isEnabled: isCatalogEnabled } = useFeature(FEATURES.CATALOG_PRODUCTS);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      if (!currentBrand?.brand_id) {
        setProducts([]);
        setPagination((prev) => prev.total === 0 ? prev : { ...prev, total: 0 });
        return;
      }
      const filters = {
        page: pagination.current,
        limit: pagination.pageSize,
      };

      const response = await getProducts(
        currentBrand?.brand_id,
        filters,
        debouncedSearch || undefined
      );
      if (response?.status === 200) {
        const productList = response?.data?.products || [];
        const nextTotal = response?.data?.total ?? productList.length ?? 0;
        setProducts(productList);
        setPagination((prev) => {
          if (prev.total === nextTotal) {
            return prev;
          }
          return {
            ...prev,
            total: nextTotal,
          };
        });
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to fetch products');
      }
    } catch (error) {
      showErrorToast(notification,'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, debouncedSearch, currentBrand?.brand_id, notification]);

  useEffect(() => {
    fetchProducts();
  }, [pagination.current, pagination.pageSize, debouncedSearch, fetchProducts]);

  useEffect(() => {
    setPagination((prev) => {
      if (prev.current === 1) {
        return prev;
      }
      return {
        ...prev,
        current: 1,
      };
    });
  }, [debouncedSearch]);

  const handleAddProduct = () => {
    setEditingProduct(null);
    form.resetFields();
    setImageUrl(null);
    setFileList((prevFiles) => {
      if (prevFiles?.[0]?.thumbUrl) {
        URL.revokeObjectURL(prevFiles[0].thumbUrl);
      }
      return [];
    });
    setImageError('');
    setPreviewVisible(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsModalVisible(true);
  };

  const navigate = useNavigate();

  const handleProductClick = (record) => {
    navigate(`/user/products/${record.id}`);
  };

  const handleEdit = (record, e) => {
    e?.stopPropagation(); // Prevent triggering row click
    setEditingProduct(record);
    form.setFieldsValue(record);
    setImageError('');
    setPreviewVisible(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (record.image_url) {
      setImageUrl(record.image_url);
      setFileList((prevFiles) => {
        if (prevFiles?.[0]?.thumbUrl) {
          URL.revokeObjectURL(prevFiles[0].thumbUrl);
        }
        return [{
          uid: '-1',
          name: 'current-image',
          status: 'done',
          url: record.image_url,
        }];
      });
    } else {
      setImageUrl(null);
      setFileList((prevFiles) => {
        if (prevFiles?.[0]?.thumbUrl) {
          URL.revokeObjectURL(prevFiles[0].thumbUrl);
        }
        return [];
      });
    }
    setIsModalVisible(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      const errorMessage = 'Image size must be 16MB or less.';
      setImageError(errorMessage);
      showErrorToast(notification, errorMessage);
      setPreviewVisible(false);
      setFileList((prevFiles) => {
        if (prevFiles?.[0]?.thumbUrl) {
          URL.revokeObjectURL(prevFiles[0].thumbUrl);
        }
        return [];
      });
      setImageUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const payload = {
      brand_id: currentBrand?.brand_id,
      path: 'products'
    };

    setImageError('');
    const previewUrl = URL.createObjectURL(file);
    const uploadedUrl = await uploadFiles(e, 'IMAGE', 16, payload);

    if (uploadedUrl) {
      setFileList((prevFiles) => {
        if (prevFiles?.[0]?.thumbUrl) {
          URL.revokeObjectURL(prevFiles[0].thumbUrl);
        }
        return [{
          uid: '-1',
          name: file.name,
          status: 'done',
          url: uploadedUrl,
          thumbUrl: previewUrl,
        }];
      });
      setImageUrl(uploadedUrl);
    } else {
      URL.revokeObjectURL(previewUrl);
      setImageUrl(null);
      setImageError('Failed to upload image. Please try again.');
      setPreviewVisible(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    setImageError('');
    setPreviewVisible(false);
    setFileList((prevFiles) => {
      if (prevFiles?.[0]?.thumbUrl) {
        URL.revokeObjectURL(prevFiles[0].thumbUrl);
      }
      return [];
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showDeleteConfirm = (id, e) => {
    e?.stopPropagation(); // Prevent triggering row click
    Modal.confirm({
      title: 'Are you sure you want to delete this product?',
      content: 'This action cannot be undone.',
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No, keep it',
      onOk: async () => {
        try {
          await deleteProduct(id, currentBrand?.brand_id);
          message.success('Product deleted successfully');
          fetchProducts();
        } catch (error) {
          message.error('Failed to delete product');
        }
      },
      onCancel() {},
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Always include image_url in the product data, even if it's being removed
      const productData = {
        ...values,
        // If we have a new uploaded file, use its URL
        // If we have an existing imageUrl but no new upload, keep it
        // If image was removed (fileList is empty and no imageUrl), set to empty string
        image_url: uploadedFileUrl || (fileList.length > 0 ? fileList[0]?.url : '') || imageUrl || ''
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData, currentBrand?.brand_id);
        showSuccessToast(notification, 'Product updated successfully');
      } else {
        await createProduct(productData, currentBrand?.brand_id);
        showSuccessToast(notification, 'Product created successfully');
      }

      setIsModalVisible(false);
      setFileList((prevFiles) => {
        if (prevFiles?.[0]?.thumbUrl) {
          URL.revokeObjectURL(prevFiles[0].thumbUrl);
        }
        return [];
      });
      setImageUrl(null);
      fetchProducts();
    } catch (error) {
      console.error('Error submitting product:', error);
      showErrorToast(notification, 'Failed to submit product');
    }
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      fixed: 'left',
      align: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div className="cursor-pointer text-left">
          <div className='flex gap-2 items-center'>
            <div className="font-medium">{text}</div>
          </div>
          {record.sku && <div className="text-gray-500 text-xs">SKU: {record.sku}</div>}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      align: 'left',
      render: (text) => (
        <div className="text-gray-600 line-clamp-2 text-left">
          {text || 'No description'}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'left',
      sorter: (a, b) => a.price - b.price,
      render: (price) => (
        <div className="font-medium text-left">
          ₹{parseFloat(price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      align: 'left',
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const statusMap = {
          10: { color: 'var(--primary)', text: 'Active' }
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return (
          <Tag color={statusInfo.color} className="capitalize">
            {statusInfo.text}
          </Tag>
        );
      },
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      align: 'left',
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
      render: (date) => (
        <div className="text-left">
          {date ? moment(date).format('MMM D, YYYY') : '-'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'left',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <div onClick={(event) => handleEdit(record, event)} className="cursor-pointer p-1 hover:bg-gray-100 rounded">
              <Edit stroke="#616874" width="1vw" height="1vw" />
            </div>
          </Tooltip>
          <Tooltip title="Delete">
            <div onClick={(event) => showDeleteConfirm(record.id, event)} className="cursor-pointer p-1 hover:bg-gray-100 rounded">
              <CustomDelete color="#F96056" width="1vw" height="1vw" />
            </div>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination((prev) => {
      if (prev.current === 1) {
        return prev;
      }
      return {
        ...prev,
        current: 1,
      };
    });
  };

  const handleTableChange = (tablePagination) => {
    setPagination((prev) => ({
      ...prev,
      current: tablePagination.current,
      pageSize: tablePagination.pageSize,
    }));
  };

  const previewSrc = fileList[0]?.thumbUrl || fileList[0]?.url || imageUrl;
  const hasImage = Boolean(previewSrc);
  const imageHelpText = imageError || (uploadError ? 'Failed to upload image. Please try again.' : defaultImageHelperText);
  const displayedProducts = useMemo(() => {
    if (!statusFilter) {
      return products;
    }
    return products.filter((product) => product.status === statusFilter);
  }, [products, statusFilter]);
  const totalRecords = statusFilter ? displayedProducts.length : pagination.total;
  const tablePaginationConfig = {
    ...pagination,
    total: totalRecords,
    showSizeChanger: true,
    showQuickJumper: false,
    showTotal: (total) => `Total ${total} products`,
    pageSizeOptions: ['15', '20', '50', '100'],
  };

  return (
    <div className="p-6">
      {isCatalogEnabled ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Products</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
              className="flex items-center bg-[--new-primary] hover:!bg-[--new-primary] text-white"
            >
              Add Product
            </Button>
          </div>

          <Card className="shadow-sm">
            <div className="mb-6">
              <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={8} lg={6}>
                  <div className="flex items-center">
                    <SearchBar
                      ref={searchInputRef}
                      searchKey={searchText}
                      setSearchKey={setSearchText}
                      onChange={handleSearch}
                      expandable={false}
                      placeholder="Search products..."
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <Table
              columns={columns}
              dataSource={displayedProducts}
              rowKey="id"
              loading={loading}
              onRow={(record) => ({
                className: 'cursor-pointer hover:bg-gray-50',
                onClick: (e) => {
                  e.stopPropagation();
                  handleProductClick(record);
                },
              })}
              pagination={tablePaginationConfig}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              className="custom-table"
              bordered
              size="middle"
            />
          </Card>
        </>
      ) : (
        <PremiumUpgradeScreen/>
      )}

      {/* Product Edit / Add Modal */}
      <Modal
        title={(
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </span>
          </div>
        )}
        open={isModalVisible}
        centered
        destroyOnClose
        width={720}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          setFileList((prevFiles) => {
            if (prevFiles?.[0]?.thumbUrl) {
              URL.revokeObjectURL(prevFiles[0].thumbUrl);
            }
            return [];
          });
          setImageUrl(null);
          setImageError('');
          setPreviewVisible(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        okText={editingProduct ? 'Update' : 'Create'}
        okButtonProps={{
          className: 'bg-[--new-primary] hover:!bg-[--new-primary] px-6 rounded-lg font-medium shadow-sm',
          size: 'large',
        }}
        cancelButtonProps={{
          size: 'large',
          className: 'rounded-lg',
        }}
        confirmLoading={uploading}
        bodyStyle={{ padding: '24px 24px 8px' }}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          className="space-y-6"
        >
          <Form.Item
            label={
              <span className="flex items-center gap-2 text-gray-700 font-medium">
                <PictureOutlined className="text-base" />
                Product Image
              </span>
            }
            help={imageHelpText}
            validateStatus={imageError ? 'error' : ''}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="relative w-full sm:w-48">
                {hasImage ? (
                  <div className="group relative">
                    <img
                      src={previewSrc}
                      alt="Product"
                      className="h-40 w-full sm:w-48 rounded-xl object-cover border border-gray-200 shadow-sm"
                      onClick={() => previewSrc && setPreviewVisible(true)}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-3 -right-3 hidden sm:flex items-center justify-center bg-white text-red-500 border border-red-500 rounded-full w-8 h-8 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <CustomDelete color="red" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full sm:w-48 h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:border-[--new-primary] hover:text-[--new-primary] transition"
                  >
                    <UploadOutlined className="text-2xl" />
                    <span className="mt-2 text-sm font-medium">Upload Image</span>
                    <span className="text-xs text-gray-400 mt-1">PNG / JPG • 16MB max</span>
                  </button>
                )}
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/75">
                    <Spin />
                  </div>
                )}
              </div>
              <div className="flex-1 min-h-[160px] rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <InfoCircleOutlined className="text-blue-500" />
                  Creative tips
                </p>
                <ul className="mt-2 text-xs text-gray-500 space-y-1 list-disc list-inside">
                  <li>Use bright, on-brand imagery to grab attention.</li>
                  <li>Supports transparent PNG or high-quality JPG up to 16MB.</li>
                </ul>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    type="primary"
                    ghost
                    icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {hasImage ? 'Replace image' : 'Choose image'}
                  </Button>
                  {hasImage && (
                    <Button
                      icon={<Eye width={16} height={16} color="var(--new-primary)" />}
                      onClick={() => previewSrc && setPreviewVisible(true)}
                    >
                      Preview
                    </Button>
                  )}
                  {hasImage && (
                    <Button
                      danger
                      ghost
                      icon={<DeleteOutlined />}
                      onClick={handleRemoveImage}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Form.Item
                name="name"
                label={
                  <span className="flex items-center gap-2 text-gray-700 font-medium">
                    <ShoppingOutlined className="text-base" />
                    Product Name
                  </span>
                }
                rules={[{ required: true, message: 'Please enter product name' }]}
              >
                <Input
                  size="large"
                  placeholder="Enter product name"
                  className="rounded-lg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="price"
                label={
                  <span className="flex items-center gap-2 text-gray-700 font-medium">
                    <DollarCircleOutlined className="text-base" />
                    Price
                  </span>
                }
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  size="large"
                  style={{ width: '100%' }}
                  stringMode
                  controls={false}
                  addonBefore="₹"
                  formatter={(value) =>
                    value
                      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      : '0.00'
                  }
                  parser={(value) => (value ? value.replace(/₹\s?|,/g, '') : '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="mt-4">
            <Form.Item
              name="description"
              label={
                <span className="flex items-center gap-2 text-gray-700 font-medium">
                  <FileTextOutlined className="text-base" />
                  Description
                </span>
              }
          >
              <Input.TextArea
                rows={4}
                placeholder="Give shoppers a crisp overview of this product"
                maxLength={400}
                showCount
                className="rounded-lg"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Product Image Preview Modal */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        width={480}
        destroyOnClose
        bodyStyle={{ padding: 0 }}
      >
        {previewSrc && (
          <div className="p-4">
            <img
              src={previewSrc}
              alt="Product preview"
              className="w-full h-full max-h-[420px] object-contain rounded-lg"
            />
          </div>
        )}
      </Modal>

      {/* Product Detail Modal */}
      <Modal
        title="Product Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={780}
        centered
        bodyStyle={{ padding: 24, background: '#f8fafc' }}
      >
        {productDetail && (
          <div className="rounded-2xl border border-gray-100 shadow-lg overflow-hidden bg-white">
            <div className="relative">
              <div className="h-40 bg-gradient-to-r from-[--new-primary]/20 via-white to-purple-100 flex items-center justify-center px-6">
                <div className="relative">
                  {productDetail.image_url ? (
                    <img
                      src={productDetail.image_url}
                      alt={productDetail.name}
                      className="h-36 w-36 rounded-2xl object-cover shadow-xl ring-4 ring-white"
                    />
                  ) : (
                    <div className="h-36 w-36 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 shadow-lg ring-4 ring-white">
                      <PictureOutlined className="text-3xl" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 pt-12 pb-6">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 capitalize">
                    {productDetail.name || 'Untitled product'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    SKU&nbsp;
                    <span className="font-medium text-gray-700">
                      {productDetail.sku || 'Not assigned'}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-2xl bg-[--new-primary]/10 text-[--new-primary] font-semibold px-5 py-2 text-lg shadow-inner">
                    {productDetail.price
                      ? `₹${parseFloat(productDetail.price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : 'Price unavailable'}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="rounded-2xl border border-gray-200 bg-white p-6">
                  <ul className="space-y-3">
                    <li className="flex justify-between items-center border-b border-gray-100 pb-2">
                      <span className="text-sm font-medium text-gray-500">Status</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {productDetail.status ? productDetail.status.toUpperCase() : 'UNKNOWN'}
                        </span>
                      </div>
                    </li>
                    {productDetail.source_type && (
                      <li className="flex justify-between items-center border-b border-gray-100 pb-2">
                        <span className="text-sm font-medium text-gray-500">Source</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {productDetail.source_type}
                          </span>
                        </div>
                      </li>
                    )}
                    <li className="flex justify-between items-center border-b border-gray-100 pb-2">
                      <span className="text-sm font-medium text-gray-500">Created</span>
                      <span className="text-sm text-gray-900">
                        {productDetail.created_at
                          ? moment(productDetail.created_at).format('MMM D, YYYY h:mm A')
                          : '-'}
                      </span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">Last Updated</span>
                      <span className="text-sm text-gray-900">
                        {productDetail.updated_at
                          ? moment(productDetail.updated_at).format('MMM D, YYYY h:mm A')
                          : '-'}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Description
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-700 whitespace-pre-line bg-gray-50 border border-gray-200 rounded-xl p-4">
                  {productDetail.description || 'No description available'}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductsList;
